import { Building2 } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { useTranslations } from "@/shared/lang/useTranslations";

import { useCardholderCopy } from "../hooks/useCardholderCopy";
import type { CardholderBoardItem } from "../types/cardholderOnboarding.types";
import {
  ACTION_LABEL_KEYS,
  cardholderDocument,
  isQueueAction,
  isSelectableRow,
} from "../utils/cardholderBoard";
import { CardholderProgress } from "./CardholderProgress";

export interface CardholderBoardTableProps {
  rows: CardholderBoardItem[];
  loading: boolean;
  providerLabel: string;
  /** Whether rows can be sent to the provider at all (its onboarding is per cardholder) and by this user. */
  canQueue: boolean;
  canWrite: boolean;
  selected: ReadonlySet<string>;
  onToggle: (identityId: string, checked: boolean) => void;
  onTogglePage: (checked: boolean) => void;
  onOpen: (item: CardholderBoardItem) => void;
  onAction: (item: CardholderBoardItem) => void;
  empty: React.ReactNode;
  footer?: React.ReactNode;
}

export function CardholderBoardTable({
  rows,
  loading,
  providerLabel,
  canQueue,
  canWrite,
  selected,
  onToggle,
  onTogglePage,
  onOpen,
  onAction,
  empty,
  footer,
}: CardholderBoardTableProps) {
  const { t } = useTranslations("cardholderOnboarding");
  const copy = useCardholderCopy();
  const selectable = rows.filter((row) => isSelectableRow(row, canQueue));
  const selectedOnPage = selectable.filter((row) => selected.has(row.identity_id)).length;

  const columns: ColocaDataTableColumn<CardholderBoardItem>[] = [
    {
      id: "select",
      header:
        selectable.length > 0 ? (
          <ColocaCheckbox
            aria-label={t("select_page")}
            checked={selectedOnPage > 0 && selectedOnPage === selectable.length}
            indeterminate={selectedOnPage > 0 && selectedOnPage < selectable.length}
            onCheckedChange={onTogglePage}
          />
        ) : null,
      headerClassName: "w-10",
      cellClassName: "w-10",
      cell: (row) =>
        isSelectableRow(row, canQueue) ? (
          <ColocaCheckbox
            aria-label={t("select_row", { name: row.name ?? row.identity_id })}
            checked={selected.has(row.identity_id)}
            onCheckedChange={(checked) => onToggle(row.identity_id, checked)}
          />
        ) : null,
    },
    {
      id: "holder",
      header: t("column_holder"),
      cellClassName: "max-w-72",
      cell: (row) => {
        const secondary = [cardholderDocument(row), row.email].filter(Boolean).join(" · ");
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="block truncate font-medium">{row.name ?? t("unnamed")}</span>
              {row.is_organization_identity && (
                <ColocaBadge variant="secondary" icon={Building2} className="shrink-0">
                  {t("own_identity_badge")}
                </ColocaBadge>
              )}
            </div>
            {secondary && <span className="block truncate text-xs text-muted-foreground">{secondary}</span>}
          </div>
        );
      },
    },
    {
      id: "progress",
      header: t("column_progress"),
      cell: (row) => (
        <CardholderProgress
          item={row}
          providerLabel={providerLabel}
          headline={copy.headline(row, providerLabel)}
        />
      ),
    },
    {
      id: "next",
      header: t("column_next"),
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) => {
        if (row.next_action === "none" || (isQueueAction(row.next_action) && !canQueue)) return null;
        const informational = row.next_action === "complete_data";
        return (
          <ColocaButton
            size="sm"
            variant={informational ? "ghost" : "default"}
            disabled={!informational && !canWrite}
            onClick={() => onAction(row)}
          >
            {t(ACTION_LABEL_KEYS[row.next_action])}
          </ColocaButton>
        );
      },
    },
  ];

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.identity_id}
      loading={loading}
      skeletonRows={8}
      minWidth={760}
      onRowClick={onOpen}
      rowClassName={(row) => (selected.has(row.identity_id) ? "bg-primary/5" : undefined)}
      empty={empty}
      footer={footer}
    />
  );
}
